import React, { useCallback } from 'react';
import { ACCOUNT_ROLES } from '@/constants/account-roles';
import { COMMUNITY_ROLES } from '@/constants/community-roles';
import { REQUEST_TYPE_LABELS, REQUEST_TYPES } from '@/constants/request-types';
import { zodResolver } from '@hookform/resolvers/zod';
import { Add, Home } from '@mui/icons-material';
import { LoadingButton } from '@mui/lab';
import {
  Alert,
  Avatar,
  Box,
  Button,
  Chip,
  FormControl,
  FormHelperText,
  Icon,
  InputLabel,
  MenuItem,
  Select,
  Stack,
  SvgIcon,
  TextField,
  Typography,
} from '@mui/material';
import Grid2 from '@mui/material/Unstable_Grid2';
import Grid from '@mui/system/Unstable_Grid';
import { DatePicker } from '@mui/x-date-pickers';
import { House } from '@phosphor-icons/react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import type { Dayjs } from 'dayjs';
import dayjs from 'dayjs';
// NEW: Import dropzone and icons

import { Controller, useForm } from 'react-hook-form';
import { toast } from 'sonner';
import { z as zod } from 'zod';

import type { Unit } from '@/types/v2/unit';
import axios from '@/lib/v2/axios';
import { useUser } from '@/hooks/v2/use-user';
import { RoleGuard } from '@/components/auth/guards/role-guard';
import { DialogActionButtons, ProfessionalDialog } from '@/components/common';
import type { FileUpload, UploadedFile } from '@/components/common/ImageUploader';
import ImageUploader from '@/components/common/ImageUploader';

// Schema for request creation including optional file attachments
const dateMessage = 'A date is required for incident or security requests';
const schema = zod
  .object({
    type: zod.enum(REQUEST_TYPES),
    subject: zod.string().min(1, 'A subject is required').max(200, 'Subject must be less than 200 characters'),
    description: zod.string().min(1, 'A description is required').min(10, 'Description must be at least 10 characters'),
    files: zod
      .array(
        zod.object({
          key: zod.string(),
          path: zod.string(),
          size: zod.number(),
          name: zod.string(),
          type: zod.string(),
        })
      )
      .optional(),
    unit_id: zod.string().optional(),
    experienced_on: zod.date({ message: dateMessage }).optional(),
    access_instructions: zod.string().optional(),
    additional_information: zod.string().optional(),
  })
  .refine(
    (data) => {
      if (['incident', 'security'].includes(data.type)) {
        return data.experienced_on !== undefined;
      }
      return true;
    },
    {
      message: dateMessage,
      path: ['experiencedOn'],
    }
  );

type Values = zod.infer<typeof schema>;

const defaultValues = {
  type: 'general',
  subject: '',
  description: '',
  files: [] as UploadedFile[],
  unit_id: '',
  experienced_on: undefined,
  access_instructions: '',
  additional_information: '',
} satisfies Values;

//
// NEW: Extend the FileUpload type to include a unique id
//
interface CreateRequestDialogProps {
  open: boolean;
  onClose: () => void;
  unitId?: string;
  communityId?: string;
}

export default function CreateRequestDialog({ open, onClose, unitId, communityId }: CreateRequestDialogProps) {
  const { community, user } = useUser();
  const queryClient = useQueryClient();
  // Compute role privileges
  const accountRoleNames = (user?.account_roles ?? []).map((r) => r.name);
  const communityRoleNames = (user?.community_roles ?? []).map((r) => r.name);
  const isAccountPrivileged = accountRoleNames.some((name) =>
    [ACCOUNT_ROLES.ACCOUNT_ADMIN, ACCOUNT_ROLES.ACCOUNT_OWNER, ACCOUNT_ROLES.ACCOUNT_MANAGER].includes(name as any)
  );
  const isCommunityManager = communityRoleNames.includes(COMMUNITY_ROLES.COMMUNITY_MANAGER);
  const hasManagerPrivileges = isAccountPrivileged || isCommunityManager;

  // Determine the effective community ID to use
  const effectiveCommunityId = communityId || community?.id;
  const effectiveCommunity = communityId ? null : community; // If communityId was passed, we'll fetch it

  // Fetch the specific community if communityId was passed but we don't have it in context
  const { data: fetchedCommunity } = useQuery({
    queryKey: ['community', communityId],
    queryFn: async () => {
      if (!communityId) return null;
      const response = await axios.get(`/api/v2/communities/${communityId}`);
      return response.data;
    },
    enabled: Boolean(communityId && !community),
  });

  // Use the fetched community or the one from context
  const currentCommunity = fetchedCommunity || effectiveCommunity;

  // Fetch specific unit if unitId is provided and not in the units list
  const { data: specificUnit } = useQuery({
    queryKey: ['unit', unitId],
    queryFn: async () => {
      if (!unitId) return null;
      const response = await axios.get(`/api/v2/units/${unitId}`);
      return response.data;
    },
    enabled: Boolean(unitId && effectiveCommunityId),
  });

  // Fetch units using useQuery
  const { data: fetchedUnits } = useQuery({
    queryKey: ['units', effectiveCommunityId],
    queryFn: async () => {
      const response = await axios.get(`/api/v2/units`, {
        params: {
          community_id: effectiveCommunityId,
        },
      });
      return response.data;
    },
    enabled: Boolean(effectiveCommunityId), // Only run query if communityId is available
  });
  // Normalize units list from API (backend already filters by role)
  const unitsList: Unit[] = React.useMemo(() => {
    if (Array.isArray(fetchedUnits)) return fetchedUnits as Unit[];
    if (fetchedUnits && Array.isArray((fetchedUnits).units)) return (fetchedUnits).units as Unit[];
    return [] as Unit[];
  }, [fetchedUnits]);

  // Backend filtering means unitsList already respects role visibility

  const {
    register,
    handleSubmit,
    watch,
    setValue,
    control,
    formState: { errors },
    reset,
  } = useForm<Values>({
    resolver: zodResolver(schema),
    defaultValues,
  });

  const requestType = watch('type');

  // Auto-select unit based on props or user's units
  React.useEffect(() => {
    if (unitId) {
      // If unitId is passed as prop, use it
      setValue('unit_id', unitId, { shouldValidate: true });
    } else if (!hasManagerPrivileges && unitsList.length === 1) {
      // Fallback to auto-selecting user's only unit
      setValue('unit_id', unitsList[0].id, { shouldValidate: true });
    }
  }, [unitId, hasManagerPrivileges, unitsList, setValue]);

  const createRequestMutation = useMutation({
    mutationFn: async (data: Values) => {
      // For non-privileged users, require a unit selection
      if (!hasManagerPrivileges) {
        if (unitsList.length === 0) {
          throw new Error('You are not associated with a unit. Contact your community manager.');
        }
        if (!data.unit_id) {
          throw new Error('Please select a unit for this request.');
        }
      }

      // Build payload
      const payload: any = { ...data };

      // Always include community_id - use effective community ID
      payload.community_id = effectiveCommunityId;

      // Map frontend fields to backend expected fields
      if (payload.subject) {
        payload.subject = payload.subject; // Keep as subject (backend expects this)
      }
      if (payload.type) {
        payload.type = payload.type; // Keep as type (backend expects this)
      }
      if (payload.experienced_on) {
        payload.experiencedOn = payload.experienced_on; // Map to experiencedOn
        delete payload.experienced_on;
      }
      // If unit_id is empty/undefined and user has privileges, treat as community-level
      if (!payload.unit_id && hasManagerPrivileges) {
        delete payload.unit_id;
      }

      const response = await axios.post('/api/v1/requests', payload);
      return response.data;
    },
  });

  const onSubmit = useCallback(
    async (data: Values) => {
      try {
        const response = await createRequestMutation.mutateAsync(data);
        if (response?.error) {
          return;
        }
        toast.success(response.message, {
          description: 'Your request has been created successfully.',
        });
        queryClient.invalidateQueries({ queryKey: ['requests', effectiveCommunityId] });
        // Also invalidate unit-specific requests if unitId is provided
        if (unitId) {
          queryClient.invalidateQueries({ queryKey: ['unit-requests', unitId, effectiveCommunityId] });
        }
        handleClose();
      } catch (error: any) {
        const message = error?.response?.data?.message || error?.message || 'Error creating request';
        toast.error(message);
      }
    },
    [createRequestMutation, queryClient, effectiveCommunityId]
  );

  // State for file uploads
  const [fileUploads, setFileUploads] = React.useState<FileUpload[]>([]);

  const handleClose = () => {
    reset();
    setFileUploads([]);
    createRequestMutation.reset();
    onClose();
  };

  // Handle upload completion
  const handleUploadComplete = (fileUploads: FileUpload[]) => {
    const successfulUploads = fileUploads
      .filter((upload) => upload.status === 'success' && upload.uploadedFile)
      .map((upload) => upload.uploadedFile!);
    setValue('files', successfulUploads, { shouldValidate: true });
  };

  // Check if any uploads are in progress
  const uploadsInProgress = fileUploads.some((upload) => upload.status === 'uploading');

  const getTypeColor = (type: string) => {
    switch (type) {
      case 'security':
        return 'error';
      case 'incident':
        return 'warning';
      case 'maintenance':
        return 'info';
      default:
        return 'primary';
    }
  };

  const mustChooseUnit = !hasManagerPrivileges && unitsList.length > 1 && !unitId;
  const hasSingleUnitAutoSelected = (!hasManagerPrivileges && unitsList.length === 1) || Boolean(unitId);
  const noUnitError = !hasManagerPrivileges && unitsList.length === 0 && !unitId;

  // Find the unit to display - use specific unit if available, otherwise search in unitsList
  const displayUnit = React.useMemo(() => {
    if (!unitId) return unitsList[0]; // For auto-selected single unit
    return specificUnit || unitsList.find((u) => u.id === unitId);
  }, [unitId, specificUnit, unitsList]);

  return (
    <ProfessionalDialog
      actions={
        <form onSubmit={handleSubmit(onSubmit)} style={{ display: 'flex', flexDirection: 'column', width: '100%' }}>
          <Box sx={{ display: 'flex', justifyContent: 'space-between', width: '100%' }}>
            <Button onClick={handleClose} variant="outlined">
              Cancel
            </Button>
            <LoadingButton
              disabled={
                uploadsInProgress ||
                createRequestMutation.isPending ||
                noUnitError ||
                (mustChooseUnit && !watch('unit_id'))
              }
              loading={uploadsInProgress || createRequestMutation.isPending}
              type="submit"
              variant="contained"
            >
              {createRequestMutation.isPending ? 'Creating...' : 'Create Request'}
            </LoadingButton>
          </Box>
        </form>
      }
      icon={<Add sx={{ fontSize: 24, color: 'primary.contrastText' }} />}
      maxWidth="md"
      onClose={handleClose}
      open={open}
      subtitle={
        unitId
          ? `Submit a new request for this unit in ${currentCommunity?.name || 'the selected community'}`
          : `Submit a new request for ${currentCommunity?.name || 'your community'}`
      }
      title="Create New Request"
    >
      <form onSubmit={handleSubmit(onSubmit)} style={{ display: 'flex', flexDirection: 'column', height: '100%' }}>
        <Stack mt={4} spacing={4}>
          {!hasManagerPrivileges && noUnitError ? <Alert severity="warning" sx={{ borderRadius: 2 }}>
              You are not associated with any units. Please contact your community manager to be assigned to a unit.
            </Alert> : null}
          <Box sx={{ mb: 4 }}>
            <Grid2 container spacing={2}>
              {/* Request Type */}
              <Grid2 lg={6} md={6} xs={12}>
                <FormControl fullWidth>
                  <InputLabel id="type-select-label">Request Type</InputLabel>
                  <Select
                    defaultValue={defaultValues.type}
                    fullWidth
                    label="Request Type"
                    labelId="type-select-label"
                    {...register('type')}
                  >
                    {REQUEST_TYPES.map((type) => (
                      <MenuItem key={type} value={type}>
                        {REQUEST_TYPE_LABELS[type]}
                      </MenuItem>
                    ))}
                  </Select>
                </FormControl>
              </Grid2>
              {/* Experienced On - for incident and security requests */}
              {['incident', 'security'].includes(requestType) && (
                <Grid2 lg={6} md={6} xs={12}>
                  <Controller
                    control={control}
                    name="experienced_on"
                    render={({ field }) => (
                      <Box>
                        <DatePicker<Dayjs>
                          label="Incident Details"
                          maxDate={dayjs()}
                          onChange={(value: Dayjs | null) => {
                            field.onChange(value ? value.toDate() : null);
                          }}
                          slotProps={{
                            textField: {
                              error: Boolean(errors.experienced_on),
                              helperText: errors.experienced_on?.message,
                              fullWidth: true,
                              sx: { borderRadius: 2 },
                            },
                          }}
                          value={field.value ? dayjs(field.value) : null}
                        />
                        <FormHelperText error={Boolean(errors.experienced_on)}>
                          When did this incident occur?
                        </FormHelperText>
                      </Box>
                    )}
                  />
                </Grid2>
              )}
            </Grid2>
          </Box>
          {/* Subject and Description */}
          <Box>
            <Grid container spacing={2}>
              <Grid xs={12}>
                <TextField
                  fullWidth
                  label="Subject"
                  placeholder="Enter a clear, concise subject for your request..."
                  {...register('subject')}
                  error={Boolean(errors.subject)}
                  helperText={errors.subject?.message || `${watch('subject')?.length || 0}/200 characters`}
                />
              </Grid>
              <Grid xs={12}>
                <TextField
                  fullWidth
                  label="Description"
                  multiline
                  placeholder="Provide a detailed description of your request. Be specific about what you need..."
                  rows={6}
                  {...register('description')}
                  error={Boolean(errors.description)}
                  helperText={errors.description?.message || `${watch('description')?.length || 0} characters (minimum 10)`}
                  sx={{
                    '& .MuiOutlinedInput-root': {
                      borderRadius: 2,
                    },
                  }}
                />
              </Grid>
            </Grid>
          </Box>

          {/* Additional Information and Access Instructions in two columns */}
          <Box>
            <Grid container spacing={3}>
              <Grid md={6} xs={12}>
                <TextField
                  fullWidth
                  label="Access Instructions"
                  multiline
                  placeholder="If access is needed (e.g., gate codes, lockbox codes, or specific entry instructions), please provide them here..."
                  rows={3}
                  {...register('access_instructions')}
                  sx={{
                    '& .MuiOutlinedInput-root': {
                      borderRadius: 2,
                    },
                  }}
                />
              </Grid>
              <Grid md={6} xs={12}>
                {/* Community Display */}
                {currentCommunity && (
                  <Box sx={{ mb: 3 }}>
                    <Typography gutterBottom sx={{ fontWeight: 600, mb: 1 }} variant="subtitle2">
                      Community
                    </Typography>
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                      <SvgIcon sx={{ backgroundColor: 'info.50', color: 'info.main', borderRadius: '50%' }}>
                        <House />
                      </SvgIcon>
                      <Stack>
                        <Typography sx={{ fontWeight: 500 }} variant="body2">
                          {currentCommunity.name}
                        </Typography>
                        <Typography color="text.secondary" variant="caption">
                          Request will be created for this community
                        </Typography>
                      </Stack>
                    </Box>
                  </Box>
                )}

                {(hasSingleUnitAutoSelected || unitId) ? <Box>
                    <Typography gutterBottom sx={{ fontWeight: 600, mb: 1 }} variant="subtitle2">
                      Unit
                    </Typography>
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                      <SvgIcon sx={{ backgroundColor: 'primary.50', color: 'primary.main', borderRadius: '50%' }}>
                        <Home />
                      </SvgIcon>
                      <Stack sx={{ flex: 1 }}>
                        <Typography sx={{ fontWeight: 500 }} variant="body2">
                          {displayUnit
                            ? `${displayUnit.unit_number ? `${displayUnit.unit_number} - ` : ''}${displayUnit.address}`
                            : 'Loading unit information...'}
                        </Typography>
                        {displayUnit && (
                          <Typography color="text.secondary" variant="caption">
                            {displayUnit.city}, {displayUnit.state} {displayUnit.zip_code}
                          </Typography>
                        )}
                      </Stack>
                    </Box>
                  </Box> : null}
                {mustChooseUnit ? <FormControl fullWidth>
                    <InputLabel id="unit-select-label">Unit</InputLabel>
                    <Select
                      defaultValue=""
                      fullWidth
                      label="Unit"
                      labelId="unit-select-label"
                      {...register('unit_id')}
                      error={Boolean(errors.unit_id) || !watch('unit_id')}
                    >
                      {(unitsList ?? []).map((unit: Unit) => (
                        <MenuItem key={unit.id} value={unit.id}>
                          <Box
                            sx={{
                              display: 'flex',
                              flexDirection: 'row',
                              alignItems: 'center',
                              gap: 1,
                              justifyContent: 'flex-start',
                            }}
                          >
                            <SvgIcon
                              sx={{
                                backgroundColor: 'primary.50',
                                color: 'primary.main',
                                borderRadius: '50%',
                              }}
                            >
                              <Home />
                            </SvgIcon>
                            <Stack>
                              <Typography sx={{ fontWeight: 500 }} variant="body2">
                                {unit.unit_number ? `${unit.unit_number} - ` : ''}{unit.address}
                              </Typography>
                              <Typography color="text.secondary" variant="caption">
                                {unit.city}, {unit.state} {unit.zip_code}
                              </Typography>
                            </Stack>
                          </Box>
                        </MenuItem>
                      ))}
                    </Select>
                    <FormHelperText error>
                      {!watch('unit_id') ? 'Please select your unit for this request.' : ' '}
                    </FormHelperText>
                  </FormControl> : null}
                {hasManagerPrivileges ? <FormControl fullWidth>
                    <InputLabel id="unit-select-label">Unit</InputLabel>
                    <Select
                      defaultValue=""
                      fullWidth
                      label="Unit"
                      labelId="unit-select-label"
                      {...register('unit_id')}
                      error={Boolean(errors.unit_id)}
                    >
                      <MenuItem value="">Community (no unit)</MenuItem>
                      {(unitsList ?? []).map((unit: Unit) => (
                        <MenuItem key={unit.id} value={unit.id}>
                          <Box
                            sx={{
                              display: 'flex',
                              flexDirection: 'row',
                              alignItems: 'center',
                              gap: 1,
                              justifyContent: 'flex-start',
                            }}
                          >
                            <SvgIcon
                              sx={{
                                backgroundColor: 'primary.50',
                                color: 'primary.main',
                                borderRadius: '50%',
                              }}
                            >
                              <Home />
                            </SvgIcon>
                            <Stack>
                              <Typography sx={{ fontWeight: 500 }} variant="body2">
                                {unit.unit_number ? `${unit.unit_number} - ` : ''}{unit.address}
                              </Typography>
                              <Typography color="text.secondary" variant="caption">
                                {unit.city}, {unit.state} {unit.zip_code}
                              </Typography>
                            </Stack>
                          </Box>
                        </MenuItem>
                      ))}
                    </Select>
                    <FormHelperText error={Boolean(errors.unit_id)}>
                      {errors.unit_id?.message ?? 'Leave empty to create a community-level request.'}
                    </FormHelperText>
                  </FormControl> : null}
              </Grid>
            </Grid>
          </Box>
          {/* File Upload */}
          <Box>
            <Typography gutterBottom sx={{ fontWeight: 600, mb: 2 }} variant="subtitle2">
              Attachments (Optional)
            </Typography>
            <ImageUploader
              maxFiles={5}
              onUploadComplete={handleUploadComplete}
              subtitle="Upload images, documents, or other files related to your request"
              title="Add Files to Request"
            />
          </Box>

          {/* Help Text for Different Request Types */}
          {requestType ? <Alert severity="info" sx={{ borderRadius: 2 }}>
              <Typography variant="body2">
                <strong>{REQUEST_TYPE_LABELS[requestType]}:</strong>{' '}
                {requestType === 'general' && 'General inquiries, suggestions, or other requests.'}
                {requestType === 'maintenance' && 'Repair requests, maintenance issues, or service needs.'}
                {requestType === 'incident' && 'Report incidents, accidents, or safety concerns. Date required.'}
                {requestType === 'security' &&
                  'Security-related issues, suspicious activity, or safety concerns. Date required.'}
              </Typography>
            </Alert> : null}
        </Stack>
      </form>
    </ProfessionalDialog>
  );
}
